import { css } from '@emotion/css';
import React from 'react';
import { useTranslation } from 'react-i18next';
import islandIcon from '../../assets/images/island.svg';
import { DreamHistory } from '../../models/dream/DreamHistory';
import { CharacterImage } from '../shared/CharacterImage';
import { getDateRelative } from '../shared/getDateRelative';
import { IslandName } from '../shared/IslandName';

interface DreamHistoryItemProps {
  item: DreamHistory;
  onClick: (item: DreamHistory) => void;
}

let DreamHistoryItem: React.FC<DreamHistoryItemProps> = ({ item, onClick }) => {
  const { t, i18n } = useTranslation(['dream', 'translation']);
  const { dreamLand, visitedAt, uploadUser } = item;
  const handleClick = () => onClick(item);

  const visitedAtRelative = getDateRelative(new Date(visitedAt * 1000));
  const visitedAtString = t(
    `translation:${visitedAtRelative.transKey}`,
    visitedAtRelative.option as any
  );

  const updatedAtRelative = getDateRelative(new Date(dreamLand.updatedAt * 1000));
  const updatedAtString = t(
    `translation:${updatedAtRelative.transKey}`,
    updatedAtRelative.option as any
  );

  return (
    <li
      className={css`
        position: relative;
        list-style: none;
        padding: 2px 0 0;
        &:last-child {
          padding-bottom: 2px;
        }
        &:nth-child(n + 2)::before,
        &:last-child::after {
          content: '';
          position: absolute;
          right: 20px;
          left: 20px;
        }
        &:nth-child(n + 2)::before {
          top: 0;
          border-top: 2px dashed #dcd8bf;
        }
        button {
          display: block;
          width: 100%;
          color: #938666;
          text-align: left;
          text-align: start;
          padding: 8px 20px;
          border-radius: 2px;
          transition: background-color 0.1s;
          &:active {
            background-color: #ece5d4;
          }
        }
        .visited-at {
          font-size: 11px;
        }
        .island {
          display: flex;
          align-items: center;
          font-size: 16px;
          color: #4d3906;
          margin: 4px 0 0;
        }
        .pict {
          flex: 0 0 auto;
          width: 20px;
          height: 20px;
          margin: 0 4px 0 0;
        }
        .land {
          flex: 1 1 auto;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
        .updated-at {
          display: flex;
          align-items: center;
          font-size: 12px;
          margin-top: 4px;
          .label {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
          .value {
            flex: 0 0 auto;
          }
        }
        .uploaded-by {
          flex: 0 0 auto;
          width: 24px;
          height: 24px;
          margin: 0 4px 0 0;
        }
        @media (max-width: 369px) {
          &:nth-child(n + 2)::before,
          &:last-child::after {
            right: 16px;
            left: 16px;
          }
          button {
            padding: 8px 16px;
          }
          .land {
            font-size: 14px;
          }
        }
      `}
    >
      <button onClick={handleClick}>
        <div className="visited-at">{t('dreamed_at', { date: visitedAtString })}</div>
        <div className="island">
          <img className="pict" src={islandIcon} alt="" aria-hidden="true" />
          <div className="land">
            {uploadUser.screening === 'ng' ? (
              t('translation:reported_player_island')
            ) : (
              <IslandName
                landName={uploadUser.land.name}
                landDisplayId={uploadUser.land.displayId}
                language={i18n.language}
              />
            )}
          </div>
        </div>

        <div className="updated-at">
          <div className="uploaded-by">
            <CharacterImage
              src={uploadUser.image}
              isPlayer={true}
              isReported={uploadUser.screening === 'ng'}
            />
          </div>
          <span className="label">{t('latest_update_00')}</span>
          <span className="value">{updatedAtString}</span>
        </div>
      </button>
    </li>
  );
};

DreamHistoryItem = React.memo(DreamHistoryItem);

export { DreamHistoryItem };
